'use client'

import { useState, useMemo, useCallback } from 'react'
import { useInView } from '@/hooks/useInView'
import { ArrowRight, Sparkles } from 'lucide-react'
import { BOOK_A_DEMO_URL } from '@/lib/constants'
import { AttributedCtaLink } from '@/components/conversion/AttributedCtaLink'

/* ── Tool Definitions ──────────────────────────────────────── */
interface ToolDef {
  name: string
  defaultCost: number
}

const DEFAULT_TOOLS: ToolDef[] = [
  { name: 'CRM / Lead Management', defaultCost: 0 },
  { name: 'Estimating Software', defaultCost: 0 },
  { name: 'Scheduling / Dispatch', defaultCost: 0 },
  { name: 'Invoicing / Payments', defaultCost: 0 },
  { name: 'Marketing Automation', defaultCost: 0 },
  { name: 'AI / Chatbot Tools', defaultCost: 0 },
  { name: 'Photo / Documentation', defaultCost: 0 },
  { name: 'Communication (SMS/Voice)', defaultCost: 0 },
]

const BUILDERLYNC_PRICE = 997

/* ── Sparkle Particle ──────────────────────────────────────── */
function SparkleParticles() {
  const particles = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        delay: Math.random() * 2,
        duration: 1.5 + Math.random() * 1.5,
        size: 2 + Math.random() * 3,
      })),
    []
  )

  return (
    <>
      <style>{`
        @keyframes sparkle-float {
          0% { opacity: 0; transform: scale(0) translateY(0); }
          50% { opacity: 1; transform: scale(1.2) translateY(-20px); }
          100% { opacity: 0; transform: scale(0) translateY(-40px); }
        }
      `}</style>
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {particles.map((p) => (
          <div
            key={p.id}
            className="absolute rounded-full bg-emerald-400"
            style={{
              left: `${p.x}%`,
              top: `${p.y}%`,
              width: p.size,
              height: p.size,
              animation: `sparkle-float ${p.duration}s ${p.delay}s ease-out infinite`,
            }}
          />
        ))}
      </div>
    </>
  )
}

/* ── Animated Number Display ──────────────────────────────── */
function AnimatedValue({
  value,
  prefix = '',
  suffix = '',
  className = '',
}: {
  value: number
  prefix?: string
  suffix?: string
  className?: string
}) {
  return (
    <span
      className={className}
      style={{ transition: 'opacity 0.3s ease' }}
    >
      {prefix}
      {value.toLocaleString()}
      {suffix}
    </span>
  )
}

/* ── Main Component ───────────────────────────────────────── */
export function ROICalculator() {
  const { ref, isInView } = useInView()

  const [costs, setCosts] = useState<Record<string, number>>(() =>
    Object.fromEntries(DEFAULT_TOOLS.map((t) => [t.name, t.defaultCost]))
  )
  const [displayValues, setDisplayValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(DEFAULT_TOOLS.map((t) => [t.name, t.defaultCost === 0 ? '' : String(t.defaultCost)]))
  )

  const handleCostChange = useCallback((name: string, value: string) => {
    // Allow empty string for clearing
    setDisplayValues((prev) => ({ ...prev, [name]: value }))
    const parsed = parseInt(value, 10)
    setCosts((prev) => ({
      ...prev,
      [name]: isNaN(parsed) ? 0 : Math.max(0, parsed),
    }))
  }, [])

  const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select()
  }, [])

  const totalCurrent = useMemo(
    () => Object.values(costs).reduce((sum, c) => sum + c, 0),
    [costs]
  )
  const monthlySavings = totalCurrent - BUILDERLYNC_PRICE
  const annualSavings = monthlySavings * 12
  const showSparkles = annualSavings > 5000

  return (
    <section className="section relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] md:w-[500px] md:h-[500px] rounded-full bg-emerald-600/[0.03] blur-[120px] pointer-events-none" />

      <div className="section-inner" ref={ref}>
        {/* ── Section Header ──────────────────────────────── */}
        <div
          className={`anim-fade-in-up text-center mb-8 sm:mb-12 ${isInView ? 'in-view' : ''}`}
        >
          <p className="text-xs uppercase tracking-[0.2em] text-red-500 font-semibold mb-4">
            ROI Calculator
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight max-w-3xl mx-auto">
            See How Much You&apos;d{' '}
            <span className="gradient-text-red">Save.</span>
          </h2>
          <p className="text-zinc-400 mt-4 text-sm sm:text-base max-w-lg mx-auto leading-relaxed">
            Enter what you&apos;re currently paying for each tool. We&apos;ll
            show your savings instantly.
          </p>
        </div>

        {/* ── Two-Column Layout ───────────────────────────── */}
        <div
          className={`anim-fade-in-up anim-delay-2 grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6 max-w-5xl mx-auto ${isInView ? 'in-view' : ''}`}
        >
          {/* ── Left: Input Form ──────────────────────────── */}
          <div className="glass-card p-4 sm:p-6 md:p-8">
            <h3 className="text-sm font-semibold text-zinc-300 uppercase tracking-wider mb-5 sm:mb-6">
              Your Current Tool Stack
            </h3>

            <div className="space-y-2.5 sm:space-y-3">
              {DEFAULT_TOOLS.map((tool) => (
                <div
                  key={tool.name}
                  className="flex items-center justify-between gap-3 py-2 px-3 sm:px-4 rounded-lg bg-zinc-900/60 border border-zinc-800/50 hover:border-zinc-700 transition-colors"
                >
                  <label
                    htmlFor={`tool-${tool.name}`}
                    className="text-xs sm:text-sm text-zinc-400 flex-1 min-w-0 truncate cursor-pointer"
                  >
                    {tool.name}
                  </label>
                  <div className="flex items-center gap-1 shrink-0">
                    <span className="text-sm text-zinc-500">$</span>
                    <input
                      id={`tool-${tool.name}`}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={displayValues[tool.name]}
                      onChange={(e) =>
                        handleCostChange(tool.name, e.target.value.replace(/[^0-9]/g, ''))
                      }
                      onFocus={handleFocus}
                      placeholder="0"
                      className="w-16 sm:w-20 bg-zinc-900 border border-zinc-800 rounded-md px-2 py-1.5 text-sm text-white text-right focus:outline-none focus:border-red-600 transition-colors placeholder:text-zinc-700"
                      aria-label={`Monthly cost for ${tool.name}`}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Total line */}
            <div className="flex items-center justify-between mt-5 pt-5 border-t border-zinc-800">
              <span className="text-sm font-semibold text-zinc-300">
                Current Monthly Total
              </span>
              <span className="text-base sm:text-lg font-bold text-red-400 tabular-nums">
                <AnimatedValue value={totalCurrent} prefix="$" suffix="/mo" />
              </span>
            </div>
          </div>

          {/* ── Right: Results Card ───────────────────────── */}
          <div className="glass-card-premium p-5 sm:p-6 md:p-8 relative">
            {/* Sparkle particles when savings > $5000/yr */}
            {showSparkles && <SparkleParticles />}

            <div className="relative z-10 space-y-5 sm:space-y-6">
              {/* Your Current Stack */}
              <div className="p-4 rounded-xl bg-zinc-900/80 border border-zinc-800">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1.5">
                  Your Current Stack
                </p>
                <p className="text-2xl sm:text-3xl font-bold text-white tabular-nums">
                  <AnimatedValue value={totalCurrent} prefix="$" />
                  <span className="text-sm text-zinc-500 font-normal">
                    /mo
                  </span>
                </p>
              </div>

              {/* BuilderLync */}
              <div className="p-4 rounded-xl bg-zinc-900/80 border border-red-600/20">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">
                    BuilderLync
                  </p>
                  <span className="text-[11px] uppercase tracking-wider font-semibold text-red-400 bg-red-600/10 px-2 py-0.5 rounded-full">
                    All-in-one
                  </span>
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-white">
                  ${BUILDERLYNC_PRICE.toLocaleString()}
                  <span className="text-sm text-zinc-500 font-normal">
                    /mo
                  </span>
                </p>
              </div>

              {/* Monthly Savings */}
              <div
                className={`p-4 rounded-xl border ${
                  monthlySavings > 0
                    ? 'bg-emerald-950/20 border-emerald-600/20'
                    : 'bg-zinc-900/80 border-zinc-800'
                }`}
              >
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1.5">
                  Monthly Savings
                </p>
                <p
                  className={`text-2xl sm:text-3xl font-bold tabular-nums ${
                    monthlySavings > 0 ? 'text-emerald-400' : 'text-zinc-400'
                  }`}
                >
                  <AnimatedValue
                    value={Math.abs(monthlySavings)}
                    prefix={monthlySavings >= 0 ? '$' : '-$'}
                  />
                  <span className="text-sm text-zinc-500 font-normal">
                    /mo
                  </span>
                </p>
              </div>

              {/* Annual Savings */}
              <div
                className={`p-4 rounded-xl border ${
                  annualSavings > 0
                    ? 'bg-emerald-950/30 border-emerald-600/30'
                    : 'bg-zinc-900/80 border-zinc-800'
                }`}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">
                    Annual Savings
                  </p>
                  {showSparkles && (
                    <div className="anim-scale-in in-view">
                      <Sparkles size={14} className="text-emerald-400" />
                    </div>
                  )}
                </div>
                <p
                  className={`text-3xl sm:text-4xl font-bold tabular-nums ${
                    annualSavings > 0 ? 'text-emerald-400' : 'text-zinc-400'
                  }`}
                >
                  <AnimatedValue
                    value={Math.abs(annualSavings)}
                    prefix={annualSavings >= 0 ? '$' : '-$'}
                  />
                  <span className="text-sm text-zinc-500 font-normal">
                    /yr
                  </span>
                </p>
              </div>

              {/* CTA */}
              <AttributedCtaLink
                href={BOOK_A_DEMO_URL}
                page="roi-calculator"
                cta="Book a Demo"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary w-full inline-flex items-center justify-center gap-2 glow-red-sm text-sm sm:text-base px-6 py-3 sm:py-4 group"
              >
                Book a Demo
                <ArrowRight
                  size={18}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </AttributedCtaLink>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
